/**
 * Actors API Client
 *
 * API functions for user actor management operations (Actor/Persona Architecture)
 */

import type { Actor, PublicActor, ActorType } from '@hummii/shared-types';
import { buildApiUrl, handleApiResponse } from '@shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

/**
 * Response types for actor operations
 */
export interface GetActorsResponse {
  actors: PublicActor[];
}

export interface SwitchActorResponse {
  message: string;
  actor: PublicActor;
}

/**
 * Get all actors for the current user
 * Backend endpoint: GET /users/me/actors
 *
 * @returns Promise<GetActorsResponse>
 */
export async function getUserActors(): Promise<GetActorsResponse> {
  const url = buildApiUrl('/users/me/actors');
  console.log('[actors.api] getUserActors - URL:', url);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    console.log('[actors.api] getUserActors - Response status:', response.status);
    console.log('[actors.api] getUserActors - Response ok:', response.ok);

    const result = await handleApiResponse<PublicActor[]>(response);
    console.log('[actors.api] getUserActors - Success:', result);

    return { actors: result };
  } catch (error) {
    console.error('[actors.api] getUserActors - Error:', error);
    throw error;
  }
}

/**
 * Switch active actor by actor ID
 * Backend endpoint: POST /users/me/switch-actor
 *
 * This will:
 * - Validate that the actor belongs to the user
 * - Generate new JWT tokens with the selected actor context
 * - Update cookies with new tokens
 *
 * @param actorId - The actor ID to switch to
 * @returns Promise<SwitchActorResponse>
 */
export async function switchActor(actorId: string): Promise<SwitchActorResponse> {
  const url = buildApiUrl('/users/me/switch-actor');
  console.log('[actors.api] switchActor - URL:', url);
  console.log('[actors.api] switchActor - ActorId:', actorId);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ actorId }),
    });

    console.log('[actors.api] switchActor - Response status:', response.status);
    console.log('[actors.api] switchActor - Response ok:', response.ok);

    const result = await handleApiResponse<SwitchActorResponse>(response);
    console.log('[actors.api] switchActor - Success:', result);
    return result;
  } catch (error) {
    console.error('[actors.api] switchActor - Error:', error);
    throw error;
  }
}

/**
 * Switch or create actor by actor type (smart mode)
 * Backend endpoint: POST /users/me/switch-actor
 *
 * This will:
 * - Find existing actor of this type OR create it if doesn't exist
 * - Generate new JWT tokens with the selected actor context
 * - Update cookies with new tokens
 *
 * Use this when you want to ensure actor exists (e.g., in RoleSwitcher)
 *
 * @param actorType - The actor type to switch to (CLIENT or WORKER)
 * @returns Promise<SwitchActorResponse>
 */
export async function switchOrCreateActorByType(actorType: ActorType): Promise<SwitchActorResponse> {
  const url = buildApiUrl('/users/me/switch-actor');
  console.log('[actors.api] switchOrCreateActorByType - URL:', url);
  console.log('[actors.api] switchOrCreateActorByType - ActorType:', actorType);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ actorType }),
    });

    console.log('[actors.api] switchOrCreateActorByType - Response status:', response.status);
    console.log('[actors.api] switchOrCreateActorByType - Response ok:', response.ok);

    const result = await handleApiResponse<SwitchActorResponse>(response);
    console.log('[actors.api] switchOrCreateActorByType - Success:', result);
    return result;
  } catch (error) {
    console.error('[actors.api] switchOrCreateActorByType - Error:', error);
    throw error;
  }
}

/**
 * Get current user profile (includes actors)
 * Backend endpoint: GET /users/me
 *
 * @returns Promise<User>
 */
export async function getCurrentUserProfile() {
  const url = buildApiUrl('/users/me');
  console.log('[actors.api] getCurrentUserProfile - URL:', url);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    console.log('[actors.api] getCurrentUserProfile - Response status:', response.status);
    console.log('[actors.api] getCurrentUserProfile - Response ok:', response.ok);

    const result = await handleApiResponse<any>(response);
    console.log('[actors.api] getCurrentUserProfile - Success:', result);
    return result;
  } catch (error) {
    console.error('[actors.api] getCurrentUserProfile - Error:', error);
    throw error;
  }
}
